import React, { useEffect, useState, useCallback } from 'react';
import {
  AnalyticalTable,
  Button,
  Dialog,
  BusyIndicator,
  Input,
  Bar,
  MessageBox,
  MessageBoxType,
  MessageBoxAction,
  Text,
  Icon,
  Label,
  Select,
  Option
} from '@ui5/webcomponents-react';
import axiosInstance from '../../utils/axiosInstance';
import useAutoTranslate from '../../hooks/useAutoTranslate';
import Swal from 'sweetalert2';

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true
});

/* ─────────────────────────────────────────
   Helpers
───────────────────────────────────────── */
const sanitizeSchema = (value) =>
  value
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[^a-z]+/, 't_')
    .substring(0, 55);

const SECTION_HEADER = {
  fontSize: '0.6875rem',
  fontWeight: '700',
  color: 'var(--sapContent_LabelColor)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: '0.75rem',
  paddingBottom: '0.4rem',
  borderBottom: '1px solid var(--sapSeparatorColor)'
};

const FIELD_LABEL = {
  display: 'block',
  fontSize: '0.8125rem',
  color: 'var(--sapContent_LabelColor)',
  marginBottom: '0.25rem'
};

const HINT_TEXT = {
  fontSize: '0.75rem',
  color: 'var(--sapContent_LabelColor)',
  marginTop: '0.2rem'
};

const ERROR_TEXT = {
  fontSize: '0.75rem',
  color: 'var(--sapNegativeColor)',
  marginTop: '0.2rem'
};

/** Renders a field: label + input + optional hint/error */
const Field = ({ label, required, hint, error, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    <span style={FIELD_LABEL}>
      {label}{required && <span style={{ color: 'var(--sapNegativeColor)', marginLeft: '2px' }}>*</span>}
    </span>
    {children}
    {error
      ? <Text style={ERROR_TEXT}>{error}</Text>
      : hint
        ? <Text style={HINT_TEXT}>{hint}</Text>
        : null}
  </div>
);

/* ─────────────────────────────────────────
   Component
───────────────────────────────────────── */
const TenantList = () => {
  const { autoT } = useAutoTranslate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [schemaManuallyEdited, setSchemaManuallyEdited] = useState(false);

  const emptyForm = { tenantKey: '', name: '', schemaName: '', contactEmail: '', domain: '', planType: 'month' };
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [migrating, setMigrating] = useState(null); // stores tenant id being migrated, or 'all'

  /* ── Data fetching ── */
  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('admin/tenants');
      const list = response.data?.code === 'SUCCESS'
        ? response.data.data
        : Array.isArray(response.data) ? response.data : [];
      setData(list || []);
    } catch {
      Toast.fire({ icon: 'error', title: autoT('Error al cargar los tenants') });
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  useEffect(() => {
    if (!searchTerm) { setFilteredData(data); return; }
    const t = searchTerm.toLowerCase();
    setFilteredData(data.filter(r =>
      r.name?.toLowerCase().includes(t) ||
      r.tenantKey?.toLowerCase().includes(t) ||
      r.schemaName?.toLowerCase().includes(t) ||
      r.contactEmail?.toLowerCase().includes(t)
    ));
  }, [data, searchTerm]);

  /* ── Form handlers ── */
  const handleNameChange = (value) => {
    setFormData(prev => ({
      ...prev,
      name: value,
      schemaName: schemaManuallyEdited ? prev.schemaName : sanitizeSchema(value)
    }));
  };

  const handleSchemaChange = (value) => {
    setSchemaManuallyEdited(true);
    setFormData(prev => ({ ...prev, schemaName: value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }));
  };

  const handleOpenCreate = () => {
    setFormData(emptyForm);
    setFormErrors({});
    setSchemaManuallyEdited(false);
    setShowDialog(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.tenantKey?.trim())
      errors.tenantKey = autoT('El tenant key es requerido');
    else if (!/^[a-z][a-z0-9_]{0,61}$/.test(formData.tenantKey))
      errors.tenantKey = autoT('Solo minúsculas, números y _ (máx. 62 chars, empieza con letra)');

    if (!formData.name?.trim())
      errors.name = autoT('El nombre es requerido');

    if (!formData.schemaName?.trim())
      errors.schemaName = autoT('El schema es requerido');
    else if (!/^[a-z][a-z0-9_]{0,61}$/.test(formData.schemaName))
      errors.schemaName = autoT('Solo minúsculas, números y _ (máx. 62 chars, empieza con letra)');

    if (!formData.contactEmail?.trim())
      errors.contactEmail = autoT('El email es requerido');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail))
      errors.contactEmail = autoT('Email inválido');

    if (!formData.domain?.trim())
      errors.domain = autoT('El dominio es requerido');

    if (!formData.planType)
      errors.planType = autoT('El tipo de plan es requerido');

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      await axiosInstance.post('admin/tenants', {
        tenantKey: formData.tenantKey,
        name: formData.name,
        schemaName: formData.schemaName,
        contactEmail: formData.contactEmail,
        domain: formData.domain,
        planType: formData.planType
      });
      fetchTenants();
      setShowDialog(false);
      Toast.fire({ icon: 'success', title: autoT('Tenant creado correctamente') });
    } catch (err) {
      Toast.fire({
        icon: 'error',
        title: err.response?.data?.message || autoT('Error al crear el tenant')
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleMigrateClick = async (tenant) => {
    setMigrating(tenant.id);
    try {
      const response = await axiosInstance.post(`admin/tenants/${tenant.id}/migrate`);
      const message = response.data?.message || autoT('Migración completada');
      Toast.fire({ icon: 'success', title: message });
    } catch (err) {
      Toast.fire({
        icon: 'error',
        title: err.response?.data?.message || autoT('Error al migrar el schema')
      });
    } finally {
      setMigrating(null);
    }
  };

  const handleMigrateAll = async () => {
    setMigrating('all');
    try {
      const response = await axiosInstance.post('admin/tenants/migrate-all');
      const message = response.data?.message || autoT('Migración completada');
      Toast.fire({ icon: 'success', title: message });
    } catch (err) {
      Toast.fire({
        icon: 'error',
        title: err.response?.data?.message || autoT('Error al migrar los schemas')
      });
    } finally {
      setMigrating(null);
    }
  };

  const handleDeleteClick = (tenant) => {
    setSelectedTenant(tenant);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTenant) return;
    try {
      await axiosInstance.delete(`admin/tenants/${selectedTenant.id}`);
      fetchTenants();
      setShowDeleteConfirm(false);
      setSelectedTenant(null);
      Toast.fire({ icon: 'success', title: autoT('Tenant eliminado correctamente') });
    } catch (err) {
      Toast.fire({
        icon: 'error',
        title: err.response?.data?.message || autoT('Error al eliminar el tenant')
      });
    }
  };

  /* ── Table columns ── */
  const columns = [
    { Header: autoT('Tenant Key'), accessor: 'tenantKey', minWidth: 130 },
    { Header: autoT('Nombre'), accessor: 'name', minWidth: 160 },
    { Header: autoT('Schema'), accessor: 'schemaName', minWidth: 130 },
    { Header: autoT('Email de Contacto'), accessor: 'contactEmail', minWidth: 190 },
    { Header: autoT('Dominio'), accessor: 'domain', minWidth: 140 },
    {
      Header: autoT('Plan'),
      accessor: 'planType',
      minWidth: 90,
      Cell: ({ value }) => <Text style={{ textTransform: 'capitalize' }}>{value || '-'}</Text>
    },
    {
      Header: autoT('Activo'),
      accessor: 'active',
      minWidth: 80,
      Cell: ({ value }) => (
        <Text style={{ color: value ? 'var(--sapPositiveColor)' : 'var(--sapNegativeColor)' }}>
          {value ? autoT('Sí') : autoT('No')}
        </Text>
      )
    },
    {
      Header: autoT('Acciones'),
      id: 'actions',
      minWidth: 100,
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '6px' }}>
          {row.original.schemaName !== 'public' && (
            <Button
              icon="synchronize"
              design="Default"
              disabled={migrating === row.original.id}
              onClick={(e) => { e.stopPropagation(); handleMigrateClick(row.original); }}
            />
          )}
          {row.original.schemaName !== 'public' && (
            <Button
              icon="delete"
              design="Negative"
              onClick={(e) => { e.stopPropagation(); handleDeleteClick(row.original); }}
            />
          )}
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <BusyIndicator active size="L" />
      </div>
    );
  }

  /* ── Render ── */
  return (
    <div>
      {/* Toolbar */}
      <Bar
        startContent={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name="search" />
            <Input
              placeholder={autoT('Buscar tenant...')}
              value={searchTerm}
              onInput={(e) => setSearchTerm(e.target.value)}
              style={{ width: '260px' }}
            />
          </div>
        }
        endContent={
          <>
            <Button
              icon="synchronize"
              design="Default"
              onClick={handleMigrateAll}
              disabled={!!migrating}
            >
              {autoT('Migrar Todos')}
            </Button>
            <Button icon="add" design="Emphasized" onClick={handleOpenCreate}>
              {autoT('Nuevo Tenant')}
            </Button>
          </>
        }
        style={{ marginBottom: '1rem' }}
      />

      <AnalyticalTable
        data={filteredData}
        columns={columns}
        visibleRows={10}
        minRows={5}
        noDataText={autoT('No hay tenants registrados')}
        alternateRowColor
      />

      {/* ── Dialog: Crear Tenant ── */}
      <Dialog
        open={showDialog}
        headerText={autoT('Nuevo Tenant')}
        footer={
          <Bar
            endContent={
              <>
                <Button design="Emphasized" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? autoT('Creando...') : autoT('Crear Tenant')}
                </Button>
                <Button design="Transparent" onClick={() => setShowDialog(false)}>
                  {autoT('Cancelar')}
                </Button>
              </>
            }
          />
        }
        onAfterClose={() => setShowDialog(false)}
        style={{ width: '600px' }}
      >
        <div style={{ padding: '1.5rem 2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          {/* ── Sección: Identificación ── */}
          <section>
            <div style={SECTION_HEADER}>{autoT('Identificación')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <Field
                label={autoT('Nombre de la Empresa')}
                required
                error={formErrors.name}
              >
                <Input
                  value={formData.name}
                  onInput={(e) => handleNameChange(e.target.value)}
                  placeholder="ACME Logística S.A."
                  valueState={formErrors.name ? 'Error' : 'None'}
                  style={{ width: '100%' }}
                />
              </Field>
              <Field
                label={autoT('Tenant Key')}
                required
                hint={autoT('Clave para integraciones externas')}
                error={formErrors.tenantKey}
              >
                <Input
                  value={formData.tenantKey}
                  onInput={(e) => setFormData(prev => ({ ...prev, tenantKey: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
                  placeholder="acme_corp"
                  valueState={formErrors.tenantKey ? 'Error' : 'None'}
                  style={{ width: '100%' }}
                />
              </Field>
            </div>
          </section>

          {/* ── Sección: Base de Datos ── */}
          <section>
            <div style={SECTION_HEADER}>{autoT('Base de Datos')}</div>
            <Field
              label={autoT('Schema PostgreSQL')}
              required
              hint={autoT('Auto-generado desde el nombre. Puede editarlo manualmente.')}
              error={formErrors.schemaName}
            >
              <Input
                value={formData.schemaName}
                onInput={(e) => handleSchemaChange(e.target.value)}
                placeholder="acme_logistica"
                valueState={formErrors.schemaName ? 'Error' : 'None'}
                style={{ width: '100%' }}
              />
            </Field>
          </section>

          {/* ── Sección: Contacto ── */}
          <section>
            <div style={SECTION_HEADER}>{autoT('Contacto')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <Field
                label={autoT('Email de Contacto')}
                required
                error={formErrors.contactEmail}
              >
                <Input
                  type="Email"
                  value={formData.contactEmail}
                  onInput={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
                  placeholder="admin@empresa.com"
                  valueState={formErrors.contactEmail ? 'Error' : 'None'}
                  style={{ width: '100%' }}
                />
              </Field>
              <Field
                label={autoT('Dominio')}
                required
                error={formErrors.domain}
              >
                <Input
                  value={formData.domain}
                  onInput={(e) => setFormData(prev => ({ ...prev, domain: e.target.value }))}
                  placeholder="empresa.com"
                  valueState={formErrors.domain ? 'Error' : 'None'}
                  style={{ width: '100%' }}
                />
              </Field>
            </div>
          </section>

          {/* ── Sección: Plan ── */}
          <section>
            <div style={SECTION_HEADER}>{autoT('Plan')}</div>
            <div style={{ width: '50%' }}>
              <Field
                label={autoT('Tipo de Plan')}
                required
                error={formErrors.planType}
              >
                <Select
                  onChange={(e) => setFormData(prev => ({ ...prev, planType: e.detail.selectedOption.value }))}
                  valueState={formErrors.planType ? 'Error' : 'None'}
                  style={{ width: '100%' }}
                >
                  <Option value="month" selected={formData.planType === 'month'}>{autoT('Mensual')}</Option>
                  <Option value="year" selected={formData.planType === 'year'}>{autoT('Anual')}</Option>
                  <Option value="trial" selected={formData.planType === 'trial'}>{autoT('Prueba')}</Option>
                  <Option value="free" selected={formData.planType === 'free'}>{autoT('Gratuito')}</Option>
                </Select>
              </Field>
            </div>
          </section>
        </div>
      </Dialog>

      {/* ── Confirmación eliminación ── */}
      <MessageBox
        type={MessageBoxType.Warning}
        open={showDeleteConfirm}
        onClose={(action) => {
          if (action === MessageBoxAction.OK) {
            handleConfirmDelete();
          } else {
            setShowDeleteConfirm(false);
            setSelectedTenant(null);
          }
        }}
        actions={[MessageBoxAction.OK, MessageBoxAction.Cancel]}
        titleText={autoT('Eliminar Tenant')}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <Text>
            {autoT('¿Está seguro que desea eliminar el tenant')} <strong>{selectedTenant?.name}</strong>?
          </Text>
          <Text style={{ color: 'var(--sapNegativeColor)', fontWeight: '600' }}>
            {autoT('Esta acción eliminará el schema PostgreSQL y TODOS sus datos de forma permanente e irreversible.')}
          </Text>
        </div>
      </MessageBox>
    </div>
  );
};

export default TenantList;
